import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../App';
import TabNavigation from '../../components/TabNavigation';
import { useAlert } from '../../context/AlertContext';

const TrainingRecords = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const dropdownRef = useRef(null);
  const highlightedRecordRef = useRef(null);
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [records, setRecords] = useState([]);
  const [courses, setCourses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [filterStatus, setFilterStatus] = useState('All');
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);

  const [scheduleForm, setScheduleForm] = useState({
    employee_id: '',
    employee_name: '',
    course_id: '',
    course_title: '',
    training_date: new Date().toISOString().split('T')[0],
    completion_status: 'Scheduled',
    trainer_name: '',
    duration_hours: '',
    notes: ''
  });

  const [completeForm, setCompleteForm] = useState({
    quiz_score: '',
    trainer_evaluation: '',
    manager_evaluation: '',
    certificate_issued: false,
    certificate_number: '',
    attendance_verified: false
  });

  // Track the passing score for the selected course
  const [coursePassingScore, setCoursePassingScore] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Scroll to highlighted record when data loads
  useEffect(() => {
    if (highlightId && records.length > 0 && highlightedRecordRef.current) {
      setTimeout(() => {
        highlightedRecordRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, [highlightId, records]);

  useEffect(() => {
    if (filterStatus === 'All') {
      setFilteredRecords(records);
    } else {
      setFilteredRecords(records.filter(r => r.completion_status === filterStatus));
    }
  }, [filterStatus, records]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowEmployeeDropdown(false);
      }
    };

    if (showEmployeeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmployeeDropdown]);

  useEffect(() => {
    if (employeeSearch.trim() === '') {
      setFilteredEmployees(employees);
    } else {
      const search = employeeSearch.toLowerCase();
      setFilteredEmployees(
        employees.filter(emp => 
          (emp.name && emp.name.toLowerCase().includes(search)) ||
          (emp.email && emp.email.toLowerCase().includes(search)) ||
          (emp.id && emp.id.toLowerCase().includes(search))
        )
      );
    }
  }, [employeeSearch, employees]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [recordsRes, coursesRes, usersRes] = await Promise.all([
        axios.get(`${API}/training-records`, { headers }),
        axios.get(`${API}/training-courses`, { headers }),
        axios.get(`${API}/users`, { headers })
      ]);

      setRecords(recordsRes.data);
      setFilteredRecords(recordsRes.data);
      setCourses(coursesRes.data);
      setEmployees(usersRes.data);
      setFilteredEmployees(usersRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const openScheduleModal = (record = null) => {
    if (record) {
      setEditingRecord(record);
      setScheduleForm({
        employee_id: record.employee_id,
        employee_name: record.employee_name,
        course_id: record.course_id,
        course_title: record.course_title,
        training_date: record.training_date.split('T')[0],
        completion_status: record.completion_status,
        trainer_name: record.trainer_name || '',
        duration_hours: record.duration_hours || '',
        notes: record.notes || ''
      });
      // Set employeeSearch to show the employee name when editing
      setEmployeeSearch(record.employee_name || '');
    } else {
      setEditingRecord(null);
      setScheduleForm({
        employee_id: '',
        employee_name: '',
        course_id: '',
        course_title: '',
        training_date: new Date().toISOString().split('T')[0],
        completion_status: 'Scheduled',
        trainer_name: '',
        duration_hours: '',
        notes: ''
      });
      // Clear employeeSearch when creating new
      setEmployeeSearch('');
    }
    setShowEmployeeDropdown(false);
    setShowScheduleModal(true);
  };

  const openCompleteModal = (record) => {
    setEditingRecord(record);
    setCompleteForm({
      quiz_score: record.quiz_score || '',
      trainer_evaluation: record.trainer_evaluation || '',
      manager_evaluation: record.manager_evaluation || '',
      certificate_issued: record.certificate_issued || false,
      certificate_number: record.certificate_number || '',
      attendance_verified: record.attendance_verified || false
    });
    // Find the course to get passing score
    const course = courses.find(c => c.id === record.course_id);
    setCoursePassingScore(course?.passing_score || null);
    setShowCompleteModal(true);
  };

  const handleScheduleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const data = {
        ...scheduleForm,
        training_date: new Date(scheduleForm.training_date).toISOString(),
        duration_hours: scheduleForm.duration_hours ? parseFloat(scheduleForm.duration_hours) : null
      };

      if (editingRecord) {
        await axios.put(
          `${API}/training-records/${editingRecord.id}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/training-records`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setShowScheduleModal(false);
      fetchData();
      showSuccess(editingRecord ? 'Training schedule updated successfully' : 'Training scheduled successfully');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to save training record');
    }
  };

  const handleCompleteSave = async () => {
    if (!editingRecord) return;

    try {
      const token = localStorage.getItem('token');
      const data = {
        ...completeForm,
        quiz_score: completeForm.quiz_score ? parseInt(completeForm.quiz_score) : null,
        trainer_evaluation: completeForm.trainer_evaluation ? parseInt(completeForm.trainer_evaluation) : null,
        manager_evaluation: completeForm.manager_evaluation ? parseInt(completeForm.manager_evaluation) : null,
        completion_status: 'Completed'
      };

      await axios.put(
        `${API}/training-records/${editingRecord.id}`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setShowCompleteModal(false);
      fetchData();
      showSuccess('Training record updated successfully');
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to update training record');
    }
  };

  const handleEmployeeSelect = (empId) => {
    const employee = employees.find(emp => emp.id === empId);
    if (employee) {
      setScheduleForm({
        ...scheduleForm,
        employee_id: empId,
        employee_name: employee.name
      });
      setEmployeeSearch(employee.name);
      setShowEmployeeDropdown(false);
    }
  };

  const handleClearEmployee = () => {
    setScheduleForm({
      ...scheduleForm,
      employee_id: '',
      employee_name: ''
    });
    setEmployeeSearch('');
    setShowEmployeeDropdown(true);
  };

  const handleCourseSelect = (e) => {
    const courseId = e.target.value;
    const course = courses.find(c => c.id === courseId);
    if (course) {
      setScheduleForm({
        ...scheduleForm,
        course_id: courseId,
        course_title: course.course_title,
        duration_hours: course.duration_hours || ''
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'bg-green-100 text-green-700';
      case 'In Progress': return 'bg-blue-100 text-blue-700';
      case 'Scheduled': return 'bg-gray-100 text-gray-700';
      case 'Failed': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/training')}
            className="text-blue-600 hover:underline mb-4 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Training Dashboard
          </button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">📋 Training Records</h1>
              <p className="text-gray-600 mt-1">{filteredRecords.length} record(s) found</p>
            </div>
            <button
              onClick={() => openScheduleModal()}
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Schedule Training
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex gap-2">
            {['All', 'Scheduled', 'In Progress', 'Completed', 'Failed'].map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-lg font-semibold ${
                  filterStatus === status
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Records Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Certificate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredRecords.map(record => (
                  <tr
                    key={record.id}
                    ref={record.id === highlightId ? highlightedRecordRef : null}
                    className={`hover:bg-gray-50 transition-colors ${
                      record.id === highlightId ? 'bg-yellow-100 ring-2 ring-yellow-400' : ''
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{record.employee_name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{record.course_title}</div>
                      {record.trainer_name && (
                        <div className="text-xs text-gray-500">Trainer: {record.trainer_name}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {new Date(record.training_date).toLocaleDateString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        getStatusColor(record.completion_status)
                      }`}>
                        {record.completion_status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {record.quiz_score ? `${record.quiz_score}%` : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {record.certificate_issued ? (
                        <span className="text-green-600">✓ Issued</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => openScheduleModal(record)}
                        className="text-blue-600 hover:underline mr-3"
                      >
                        Edit Schedule
                      </button>
                      {record.completion_status === 'Completed' ? (
                        <button
                          onClick={() => openCompleteModal(record)}
                          className="text-purple-600 hover:underline"
                        >
                          Edit Scores
                        </button>
                      ) : (
                        <button
                          onClick={() => openCompleteModal(record)}
                          className="text-green-600 hover:underline"
                        >
                          Complete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredRecords.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600">No training records found</p>
            </div>
          )}
        </div>

        {/* Schedule Training Modal */}
        {showScheduleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-800">
                  {editingRecord ? 'Update Training Schedule' : 'Schedule Training'}
                </h2>
                <button onClick={() => setShowScheduleModal(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Employee Selection with Search */}
                  <div className="relative" ref={dropdownRef}>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Employee <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={employeeSearch}
                        onChange={(e) => setEmployeeSearch(e.target.value)}
                        onFocus={() => !editingRecord && setShowEmployeeDropdown(true)}
                        disabled={!!editingRecord}
                        placeholder="Search by name, email, or ID..."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                      />
                      {scheduleForm.employee_id && !editingRecord && (
                        <button
                          type="button"
                          onClick={handleClearEmployee}
                          className="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                    {showEmployeeDropdown && !editingRecord && filteredEmployees.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredEmployees.slice(0, 50).map(emp => (
                          <button
                            key={emp.id}
                            type="button"
                            onClick={() => handleEmployeeSelect(emp.id)}
                            className="w-full text-left px-4 py-2 hover:bg-blue-50 flex justify-between items-center border-b border-gray-100 last:border-b-0"
                          >
                            <div>
                              <div className="font-semibold text-gray-800">{emp.name || 'Unknown'}</div>
                              <div className="text-xs text-gray-500">{emp.email || emp.id || 'N/A'}</div>
                            </div>
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">{emp.role || 'User'}</span>
                          </button>
                        ))}
                        {filteredEmployees.length > 50 && (
                          <div className="px-4 py-2 text-xs text-gray-500 text-center bg-gray-50">
                            Showing first 50 results. Type to refine search.
                          </div>
                        )}
                      </div>
                    )}
                    {scheduleForm.employee_id && (
                      <p className="text-xs text-green-600 mt-1">✓ Selected: {scheduleForm.employee_name}</p>
                    )}
                  </div>

                  {/* Course Selection */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Training Course <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={scheduleForm.course_id}
                      onChange={handleCourseSelect}
                      disabled={!!editingRecord}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100"
                    >
                      <option value="">Select Course</option>
                      {courses.map(course => (
                        <option key={course.id} value={course.id}>
                          {course.course_title} ({course.training_type})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Training Date */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Training Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={scheduleForm.training_date}
                      onChange={(e) =>
                        setScheduleForm({ ...scheduleForm, training_date: e.target.value })
                      }
                      min={new Date().toISOString().split("T")[0]}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={scheduleForm.completion_status}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, completion_status: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="Scheduled">Scheduled</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                    </select>
                  </div>

                  {/* Trainer Name */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Trainer Name</label>
                    <input
                      type="text"
                      value={scheduleForm.trainer_name}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, trainer_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  {/* Duration */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Duration (hours)</label>
                    <input
                      type="number"
                      step="0.5"
                      value={scheduleForm.duration_hours}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, duration_hours: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  {/* Notes */}
                  <div className="col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Notes</label>
                    <textarea
                      value={scheduleForm.notes}
                      onChange={(e) => setScheduleForm({ ...scheduleForm, notes: e.target.value })}
                      rows={3}
                      placeholder="Any special instructions or requirements..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleScheduleSave}
                  disabled={!scheduleForm.employee_id || !scheduleForm.course_id}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400"
                >
                  {editingRecord ? 'Update Schedule' : 'Schedule Training'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Complete Training Modal */}
        {showCompleteModal && editingRecord && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {editingRecord.completion_status === 'Completed' ? 'Edit Training Scores' : 'Complete Training'}
                  </h2>
                  <p className="text-sm text-gray-600 mt-1">
                    {editingRecord.employee_name} - {editingRecord.course_title}
                  </p>
                </div>
                <button onClick={() => setShowCompleteModal(false)} className="text-gray-500 hover:text-gray-700">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                  <p className="text-sm text-blue-700">
                    <strong>Training Date:</strong> {new Date(editingRecord.training_date).toLocaleDateString()}
                  </p>
                  {editingRecord.trainer_name && (
                    <p className="text-sm text-blue-700">
                      <strong>Trainer:</strong> {editingRecord.trainer_name}
                    </p>
                  )}
                </div>

                {/* Warning when score is below passing */}
                {coursePassingScore !== null && completeForm.quiz_score !== '' && parseInt(completeForm.quiz_score) < coursePassingScore && (
                  <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
                    <p className="text-sm text-red-700">
                      <strong>Warning:</strong> The quiz score ({completeForm.quiz_score}%) is below the passing score ({coursePassingScore}%).
                      This training record will be marked as <strong>"Failed"</strong>.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  {/* Quiz Score */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Quiz Score (%)</label>
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={completeForm.quiz_score}
                      onChange={(e) => setCompleteForm({ ...completeForm, quiz_score: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                    {coursePassingScore !== null && (
                      <div className="mt-1">
                        <span className="text-xs text-gray-500">Pass Mark: {coursePassingScore}%</span>
                        {completeForm.quiz_score !== '' && (
                          <span className={`ml-2 text-xs font-semibold ${
                            parseInt(completeForm.quiz_score) >= coursePassingScore
                              ? 'text-green-600'
                              : 'text-red-600'
                          }`}>
                            {parseInt(completeForm.quiz_score) >= coursePassingScore ? '✓ Pass' : '✗ Fail'}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Trainer Evaluation */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Trainer Evaluation (1-5)
                    </label>
                    <select
                      value={completeForm.trainer_evaluation}
                      onChange={(e) => setCompleteForm({ ...completeForm, trainer_evaluation: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Not Rated</option>
                      <option value="1">1 - Poor</option>
                      <option value="2">2 - Below Average</option>
                      <option value="3">3 - Average</option>
                      <option value="4">4 - Good</option>
                      <option value="5">5 - Excellent</option>
                    </select>
                  </div>

                  {/* Manager Evaluation */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Manager Evaluation (1-5)
                    </label>
                    <select
                      value={completeForm.manager_evaluation}
                      onChange={(e) => setCompleteForm({ ...completeForm, manager_evaluation: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Not Rated</option>
                      <option value="1">1 - Poor</option>
                      <option value="2">2 - Below Average</option>
                      <option value="3">3 - Average</option>
                      <option value="4">4 - Good</option>
                      <option value="5">5 - Excellent</option>
                    </select>
                  </div>

                  {/* Certificate Number */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Certificate Number</label>
                    <input
                      type="text"
                      value={completeForm.certificate_number}
                      onChange={(e) => setCompleteForm({ ...completeForm, certificate_number: e.target.value })}
                      placeholder="e.g., CERT-2024-001"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>

                  {/* Checkboxes */}
                  <div className="col-span-2 space-y-3">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={completeForm.attendance_verified}
                        onChange={(e) => setCompleteForm({ ...completeForm, attendance_verified: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-semibold text-gray-700">Attendance Verified</span>
                    </label>

                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={completeForm.certificate_issued}
                        onChange={(e) => setCompleteForm({ ...completeForm, certificate_issued: e.target.checked })}
                        className="w-4 h-4"
                      />
                      <span className="text-sm font-semibold text-gray-700">Certificate Issued</span>
                    </label>

                    {completeForm.certificate_issued && completeForm.certificate_number && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-xs text-green-700">
                          <strong>ℹ️ Note:</strong> A certification will be automatically added to the Certifications log with a 1-year validity period.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowCompleteModal(false)}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCompleteSave}
                  className={`px-6 py-2 text-white rounded-lg ${
                    editingRecord?.completion_status === 'Completed'
                      ? 'bg-purple-600 hover:bg-purple-700'
                      : coursePassingScore !== null && completeForm.quiz_score !== '' && parseInt(completeForm.quiz_score) < coursePassingScore
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-green-600 hover:bg-green-700'
                  }`}
                >
                  {editingRecord?.completion_status === 'Completed'
                    ? 'Update Scores'
                    : coursePassingScore !== null && completeForm.quiz_score !== '' && parseInt(completeForm.quiz_score) < coursePassingScore
                      ? 'Mark as Failed'
                      : 'Mark as Completed'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      </div>
  );
};

export default TrainingRecords;